import {backend} from "../../../_services";
import {useAuth} from "../../../context";
import {useEffect, useState} from "react";
import {config} from "../../../utils2";
import {fetchWrapper} from "../../../_services/fetch";
import {useSearchParams} from "react-router-dom";
import * as yup from "yup";
import {regex} from "../../../utils2";

const api = config.get_remote_api() + "/api/accounts"

const endpointOrderOptions = () => `${api}/order`
const endpointDetails = (targetId) => `${api}/${targetId}`
const endpointCount = (search) => `${api}/count${fetchWrapper.getSearchParams(search)}`
const endpointList = (search) => `${api}/list${fetchWrapper.getSearchParams(search)}`

const endpointAdd = () => `${api}/create`
const endpointEdit = (targetId) => `${api}/${targetId}`
const endpointDelete = (targetId) => `${api}/${targetId}`

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null

export function useUser(uid = null) {
    const role = [
        {name: 'User', value: 'user'},
        {name: 'Admin', value: 'admin'}
    ]
    const plan = [
        {name: 'Free', value: 'free'},
        {name: 'Basic', value: 'basic'},
        {name: 'Premium', value: 'premium'}
    ]

    const defaultValues = {
        id: null,
        username: "",
        password: "",
        first_name: "",
        last_name: "",
        address_count: 0,
        location: "",
        plan: "",
        device_count: 0,
        tag_count: 0,
        order_count: 0,
        verified: false,
        blocked: false,
        role: "",
        created_at: null,
        updated_at: null,
        verified_at: null,
        accessed_at: null,
    }

    const validationSchema = yup.object().shape({
        username: yup.string()
            .required("Email is mandatory")
            .email("Email not valid"),
        role: yup.string()
            .required("Role is mandatory")
            .oneOf(role.map(role => role.value), "Unexpected role"),
        password: yup.string()
            // .required("Password is mandatory")
            .test("required", "A valid password is required", value => !(uid === "add" && (value === "" || value === undefined)))
            // .min(8, 'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special')
            .matches(regex.password_pattern, "Create a stronger password"),
        first_name: yup.string()
            .required("First name is missing"),
        last_name: yup.string()
            .required("Last name is missing"),
        // location: yup.string(),
        plan: yup.string()
            .required("Plan is mandatory")
            .oneOf(plan.map(role => role.value), "Unexpected plan"),
    })

    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useUserDetails(uid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }


    function addItem(formValue) {
        const {id, ...valuesNoId} = formValue
        return fetchWrapper.post(endpointAdd(), valuesNoId, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = uid) {
        return fetchWrapper.put(endpointEdit(targetId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = uid) {
        return fetchWrapper.delete(endpointDelete(targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }


    return {
        value,
        validationSchema,
        addItem,
        editItem,
        addEditItem: values => uid === "add" ? addItem(values) : editItem(values),
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null),
        role,
        plan,
    }
}

export function useUserDetails(accountId, defaultValue) {

    const {loggedIn, token} = useAuth()
    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && accountId && accountId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(accountId), requestOptions)
                .then(handleSuccess)
                .then(data => setValue({...data, password: ""}))
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [loggedIn, accountId]);

    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useUserCount(queryOption = null) {

    const {loggedIn, token} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            fetchWrapper.generic(endpointCount(search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(err => setErrorCount(handleError(err)))
                .finally(() => setLoadingCount(false))
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}

export function useUserList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const defaultPageSize = 10
    const defaultPageIndex = 0

    const {loggedIn, token} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            fetchWrapper.generic(endpointList(search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useUserOrderOptions(defaultOptions = []) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(userId), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}

// Old code to delete when the new hook will be completed

// export function useUserDetails(userId) {
//     return useBackendDetails(backend.getUserDetails, userId)
// }

// export function useUserCount(userId, query = null) {
//     return useBackendCount(backend.getUserCount, userId, query)
// }

// export function useUserList(userId, count, pageIndex = 0, pageSize = 100, query = null, order_by = null) {
//     return useBackendList(backend.getUserList, userId, count, pageIndex, pageSize, query, order_by)
// }


export function useUserActions(userId) {

    const {loggedIn, token} = useAuth()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    function handleSuccess(resp) {
        if (resp.success) {
            return Promise.resolve(resp?.data);
        } else {
            if ("message" in resp) {
                return Promise.reject({message: resp.message});
            } else {
                return Promise.reject({message: "Failed with no error messages from the server."});
            }
        }
    }

    function handleError(err) {
        if (err?.name !== "AbortError") {
            setError(`Fetch failed due to ${err?.message}`)
            return Promise.reject(JSON.stringify(err));
        }
    }

    function handleFinally() {
        setLoading(false)
    }

    function get(targetId) {
        return backend.getUserDetails(token.jwt, targetId)
            .then(handleSuccess)
            .catch(handleError)
            .finally(handleFinally)
    }

    function add(first_name, last_name, location, username, password, role, verified, blocked) {
        const payload = {first_name, last_name, location, username, password, role, verified, blocked}
        return backend.addUser(token.jwt, userId, payload)
            .then(handleSuccess)
            .catch(handleError)
            .finally(handleFinally)
    }

    function edit(targetId, first_name, last_name, location, username, password, role = null, verified = null, blocked = null) {
        const payload = {first_name, last_name, location, username, password, role, verified, blocked}
        return backend.editUser(token.jwt, userId, targetId, payload)
            .then(handleSuccess)
            .catch(handleError)
            .finally(handleFinally)
    }

    function remove(targetId) {
        return backend.deleteUser(token.jwt, userId, targetId)
            .then(handleSuccess)
            .catch(handleError)
            .finally(handleFinally)
    }

    return [get, add, edit, remove, loading, error]
}